import { ApiRequest } from '../baseService'

const BASE_ROUTE = 'app/eml'
const APPOINTMENT_ROUTE = `${BASE_ROUTE}/appointments`

const GetAppointmentById = (emlId: string) => {
  return ApiRequest.get(`${APPOINTMENT_ROUTE}/${emlId}`)
}

const SendNotification = (data: any) => {
  return ApiRequest.post(`${BASE_ROUTE}/notification`, data)
}

const SyncSpecialist = (specialistId: string) => {
  return ApiRequest.post(`${BASE_ROUTE}/sync-specialist/${specialistId}`)
}
const SyncSpecialistData = (specialistId: string) => {
  return ApiRequest.post(`${BASE_ROUTE}/sync-specialist-data/${specialistId}`)
}
const ViewSyncedSpecialistData = (emlSpecialistId: string) => {
  return ApiRequest.get(`${BASE_ROUTE}/specialists/${emlSpecialistId}`)
}

const GetBookingTypes = () => {
  return ApiRequest.get(`${BASE_ROUTE}/booking-types`)
}

const SyncAllSpecialists = () => {
  return ApiRequest.post(`${BASE_ROUTE}/sync-all-specialists`)
}

const BulkSyncAppointments = () => {
  return ApiRequest.post(`${BASE_ROUTE}/bulk-sync-appointments`)
}

const GetSyncedAppointments = (params: any) => {
  return ApiRequest.get(`${BASE_ROUTE}/synced-appointments`, { params })
}

const GetEmlAppointments = (params: any) => {
  return ApiRequest.get(`${BASE_ROUTE}/eml-appointments`, { params })
}

const GetSyncedReport = () => {
  return ApiRequest.get(`${BASE_ROUTE}/synced-report`)
}

const DeleteSpecialistEmlAppointments = (emlSpecialistId: string) => {
  return ApiRequest.delete(
    `${BASE_ROUTE}/specialists/${emlSpecialistId}/appointments`
  )
}

const DeleteEmlSpecialist = (emlSpecialistId: string) => {
  return ApiRequest.delete(`${BASE_ROUTE}/specialists/${emlSpecialistId}`)
}

const FetchAllEmlAppointments = () => {
  return ApiRequest.post(`${BASE_ROUTE}/fetch-appointments?totalItem=200`)
}

const SyncAppointmentData = (appointmentId: string) => {
  return ApiRequest.post(`${BASE_ROUTE}/appointments/${appointmentId}`)
}

const DeleteEmlAppointment = (emlAppointmentId: string) => {
  return ApiRequest.delete(`${BASE_ROUTE}/eml-appointments/${emlAppointmentId}`)
}

const ConfirmBookAppointment = (appointmentId: string) => {
  return ApiRequest.post(
    `${BASE_ROUTE}/appointments/${appointmentId}/confirm-book`
  )
}

const ConfirmBookAppointmentFromMAG = (appointmentId: string) => {
  return ApiRequest.post(
    `${BASE_ROUTE}/appointments/${appointmentId}/confirm-book-from-mag`
  )
}
const MarkAsNotAvailable = (emlAppointmentId: string) => {
  return ApiRequest.post(
    `${BASE_ROUTE}/eml-appointments/${emlAppointmentId}/mark-as-not-available`
  )
}

const SyncAcceptExcludeSessions = () => {
  return ApiRequest.post(`${BASE_ROUTE}/sync-exclude-sessions`)
}

const GetAppointmentReport = (params: any) => {
  return ApiRequest.get(`${BASE_ROUTE}/report-eml-appointments`, { params })
}

// Every EML data problem Kawaconn can see, with what to do about each.
const GetDataIssues = (params: {
  kind?: string
  severity?: string
  // Matched server-side against the EML appointment, clinic and session ids, whole or partial.
  searchId?: string
  includeResolved?: boolean
}) => {
  return ApiRequest.get(`${BASE_ROUTE}/data-issues`, { params })
}

// Withdraw EML appointments Kawaconn has lost track of. Answers per item: a batch normally mixes
// ones that release cleanly with ones EML refuses.
const ReleaseDataIssueAppointments = (emlIds: string[]) => {
  return ApiRequest.post(`${BASE_ROUTE}/data-issues/release`, { emlIds })
}

// Per-record endings for an issue. Dismiss and Restore touch Kawaconn only; CancelOnEml cancels the
// appointment on EML first and is Admin Staff only. All three require a reason.
const DismissDataIssue = (data: any) => {
  return ApiRequest.post(`${BASE_ROUTE}/data-issues/dismiss`, data)
}

const RestoreDataIssue = (data: any) => {
  return ApiRequest.post(`${BASE_ROUTE}/data-issues/restore`, data)
}

const CancelDataIssueOnEml = (data: any) => {
  return ApiRequest.post(`${BASE_ROUTE}/data-issues/cancel-on-eml`, data)
}

// Lifts a Tentative hold so the slot is on offer again. Milder than a cancel and open to Staff:
// it puts a slot back on sale rather than taking an appointment away.
const ReleaseDataIssueHold = (data: any) => {
  return ApiRequest.post(`${BASE_ROUTE}/data-issues/release-hold`, data)
}

// Bulk counterparts. Dismiss is allowed in bulk because it is recoverable; cancelling on EML is
// not, and deliberately has no bulk form.
const DismissDataIssues = (items: any[], reason: string) => {
  return ApiRequest.post(`${BASE_ROUTE}/data-issues/dismiss-many`, {
    items,
    reason,
  })
}

const SyncDataIssueSessions = (sessionIds: string[]) => {
  return ApiRequest.post(`${BASE_ROUTE}/data-issues/sync-now`, { sessionIds })
}

// Clear session links to EML appointments a reschedule replaced. Local only — EML is not contacted.
// Capped per run; the response reports how many are left.
const CleanUpSupersededEmlLinks = () => {
  return ApiRequest.post(`${BASE_ROUTE}/data-issues/clean-superseded-links`)
}

// Re-check one reported issue against live EML data. At most two EML calls, made server-side;
// answers Resolved / StillBroken / Inconclusive.
const VerifyDataIssue = (payload: {
  kind: string
  emlId?: string
  sessionId?: string
  emlClinicId?: string
}) => {
  return ApiRequest.post(`${BASE_ROUTE}/data-issues/verify`, payload)
}

const RemoveOrphanedSessions = (emlSessionIds: string[]) => {
  return ApiRequest.post(`${BASE_ROUTE}/remove-orphaned-sessions`, {
    emlSessionIds: emlSessionIds,
  })
}

const RemoveEmlAppointment = (emlSessionId: string) => {
  return ApiRequest.delete(`${BASE_ROUTE}/appointments/${emlSessionId}`)
}

// Full EML sync diagnostic (state + per-rule checks) for a single appointment session.
// verifyWithEml asks the backend to read the live EML status (popup open / after a re-sync);
// the default keeps badge loads free of EML calls.
const GetSessionSyncStatus = (sessionId: string, verifyWithEml = false) => {
  return ApiRequest.get(`${BASE_ROUTE}/appointments/${sessionId}/sync-status`, {
    params: { verifyWithEml },
  })
}

// Manually (re)sync a single session to EML. Backend validates eligibility first and returns a
// UserFriendlyException listing the failed rules when the session cannot be synced.
const ManualSyncToEml = (sessionId: string) => {
  return ApiRequest.post(`${BASE_ROUTE}/appointments/${sessionId}/sync-now`)
}

// List sessions that are eligible but not synced, or linked but no longer eligible.
const GetPendingSyncSessions = (params: any) => {
  return ApiRequest.get(`${BASE_ROUTE}/pending-sync-appointments`, { params })
}

const API = {
  GetAppointmentById,
  SendNotification,
  SyncSpecialistData,
  SyncSpecialist,
  ViewSyncedSpecialistData,
  GetBookingTypes,
  SyncAllSpecialists,
  BulkSyncAppointments,
  GetSyncedAppointments,
  GetEmlAppointments,
  GetSyncedReport,
  DeleteSpecialistEmlAppointments,
  DeleteEmlSpecialist,
  FetchAllEmlAppointments,
  SyncAppointmentData,
  DeleteEmlAppointment,
  ConfirmBookAppointment,
  ConfirmBookAppointmentFromMAG,
  MarkAsNotAvailable,
  SyncAcceptExcludeSessions,
  RemoveOrphanedSessions,
  GetAppointmentReport,
  GetDataIssues,
  ReleaseDataIssueAppointments,
  CleanUpSupersededEmlLinks,
  DismissDataIssue,
  RestoreDataIssue,
  CancelDataIssueOnEml,
  ReleaseDataIssueHold,
  DismissDataIssues,
  SyncDataIssueSessions,
  VerifyDataIssue,
  RemoveEmlAppointment,
  GetSessionSyncStatus,
  ManualSyncToEml,
  GetPendingSyncSessions,
}

export default API
